/**
 * crosstable-renderer.ts - Crosstable Rendering for Round Robin Tournaments
 *
 * Renders a matrix showing head-to-head results between all players.
 */

import type { Tournament, Player, Match, Standing, TiebreakCriterion } from '../../../types/api';
import { TIEBREAK_DEFINITIONS, DEFAULT_TIEBREAKS_BY_FORMAT } from '../../../types/api';
import { escapeHtml } from '../../utils/security';
import { t } from '../../utils/i18n-helper';

/**
 * Get shortened name for column header (last name or initials)
 */
function getShortName(player: Player): string {
  const firstName = player.firstName || '';
  const lastName = player.lastName || '';

  // If last name exists, use it (truncated if needed)
  if (lastName) {
    return lastName.length > 8 ? lastName.substring(0, 7) + '.' : lastName;
  }
  // Otherwise use first name
  if (firstName) {
    return firstName.length > 8 ? firstName.substring(0, 7) + '.' : firstName;
  }
  return '?';
}

/**
 * Get result display for a match from player's perspective
 * @param result - The match result (1-0, 0-1, 0.5-0.5, etc.)
 * @param isPlayer1 - Whether the player is player1 (white)
 * @returns Display string (1, 0, ½, or empty)
 */
function getResultDisplay(result: string | undefined, isPlayer1: boolean): string {
  if (!result || result === '' || result === 'null') return '';

  // Handle forfeits
  const normalizedResult = result.toLowerCase().replace('ff', '');

  if (normalizedResult === '1-0') {
    return isPlayer1 ? '1' : '0';
  }
  if (normalizedResult === '0-1') {
    return isPlayer1 ? '0' : '1';
  }
  if (normalizedResult === '0.5-0.5' || normalizedResult === '½-½') {
    return '½';
  }
  if (normalizedResult === '0-0') {
    return '0';
  }

  return '';
}

/**
 * Build a lookup map for matches between players
 * @param matches - All matches in the tournament
 * @returns Map of player1Id -> player2Id -> result info
 */
function buildMatchLookup(matches: Match[]): Map<string, Map<string, { result: string; isPlayer1: boolean; round: number }[]>> {
  const lookup = new Map<string, Map<string, { result: string; isPlayer1: boolean; round: number }[]>>();

  for (const match of matches) {
    if (!match.player1?.id || !match.player2?.id) continue;
    if (match.isBye) continue;

    const p1Id = match.player1.id;
    const p2Id = match.player2.id;

    // Add entry for player1 -> player2
    if (!lookup.has(p1Id)) {
      lookup.set(p1Id, new Map());
    }
    const p1Opponents = lookup.get(p1Id)!;
    if (!p1Opponents.has(p2Id)) {
      p1Opponents.set(p2Id, []);
    }
    p1Opponents.get(p2Id)!.push({ result: match.result || '', isPlayer1: true, round: match.round });

    // Add entry for player2 -> player1
    if (!lookup.has(p2Id)) {
      lookup.set(p2Id, new Map());
    }
    const p2Opponents = lookup.get(p2Id)!;
    if (!p2Opponents.has(p1Id)) {
      p2Opponents.set(p1Id, []);
    }
    p2Opponents.get(p1Id)!.push({ result: match.result || '', isPlayer1: false, round: match.round });
  }

  return lookup;
}

/**
 * Render the crosstable for a round robin tournament
 * @param tournament - Tournament object
 * @param standings - Standings array (sorted by rank)
 * @param tournamentId - The tournament ID
 * @returns HTML string for the crosstable
 */
export function renderCrosstable(
  tournament: Tournament,
  standings: Standing[],
  tournamentId: string
): string {
  if (!standings || standings.length === 0) {
    return `<p>${t('crosstable.noData', 'No crosstable data available.')}</p>`;
  }

  const matches = tournament.matches || [];
  const matchLookup = buildMatchLookup(matches);

  // Get players sorted by standings rank
  const players = standings.map(s => s.player).filter((p): p is Player => !!p);

  if (players.length === 0) {
    return `<p>${t('crosstable.noPlayers', 'No players in crosstable.')}</p>`;
  }

  // Create player index map for column headers
  const playerIndexMap = new Map<string, number>();
  players.forEach((p, idx) => {
    playerIndexMap.set(p.id, idx + 1);
  });

  // Get tiebreak criteria for this tournament
  const tiebreakCriteria: TiebreakCriterion[] = tournament.tiebreakCriteria || DEFAULT_TIEBREAKS_BY_FORMAT['round-robin'] || [];

  // Build header row with player names
  let headerCells = '<th class="crosstable-rank">#</th>';
  headerCells += `<th class="crosstable-player">${t('standings.player', 'Player')}</th>`;
  for (let i = 0; i < players.length; i++) {
    const player = players[i];
    const shortName = getShortName(player);
    headerCells += `<th class="crosstable-col-num" title="${escapeHtml(`${player.firstName || ''} ${player.lastName || ''}`.trim())}"><span class="crosstable-col-index">${i + 1}</span><span class="crosstable-col-name">${escapeHtml(shortName)}</span></th>`;
  }
  headerCells += `<th class="crosstable-points">${t('standings.points', 'Pts')}</th>`;

  // Add tiebreak columns
  for (const criterion of tiebreakCriteria) {
    const def = TIEBREAK_DEFINITIONS[criterion];
    if (def) {
      headerCells += `<th class="crosstable-tiebreak">${def.abbrev}</th>`;
    }
  }

  // Build rows
  let rows = '';
  standings.forEach((standing, rowIdx) => {
    const player = standing.player;
    if (!player) return;

    const playerNum = rowIdx + 1;
    const playerName = `${player.firstName || ''} ${player.lastName || ''}`.trim() || 'Unknown';
    const playerRating = player.rating ? `<span class="crosstable-rating">${player.rating}</span>` : '';

    let cells = `<td class="crosstable-rank">${playerNum}</td>`;
    cells += `<td class="crosstable-player">${escapeHtml(playerName)}${playerRating}</td>`;

    // Results against each opponent
    for (let colIdx = 0; colIdx < players.length; colIdx++) {
      const opponent = players[colIdx];
      if (!opponent) continue;

      if (opponent.id === player.id) {
        // Diagonal - player vs self (dark background, no content)
        // Inline styles ensure background prints correctly
        cells += '<td class="crosstable-cell crosstable-diagonal" style="background-color: #4a4a4a !important; -webkit-print-color-adjust: exact; print-color-adjust: exact;"></td>';
      } else {
        // Get result(s) against this opponent
        const playerMatches = matchLookup.get(player.id);
        const resultsVsOpponent = playerMatches?.get(opponent.id) || [];

        if (resultsVsOpponent.length === 0) {
          cells += '<td class="crosstable-cell crosstable-empty"></td>';
        } else {
          // Sort by round and display all results
          resultsVsOpponent.sort((a, b) => a.round - b.round);
          const resultDisplays = resultsVsOpponent.map(r => {
            const display = getResultDisplay(r.result, r.isPlayer1);
            return display || '-';
          });

          // Format: single result or multiple (for double round robin)
          const displayText = resultDisplays.join(' ');
          const cellClass = getCellClass(resultDisplays);
          cells += `<td class="crosstable-cell ${cellClass}">${displayText}</td>`;
        }
      }
    }

    // Total points
    cells += `<td class="crosstable-points"><strong>${standing.points}</strong></td>`;

    // Add tiebreak values
    for (const criterion of tiebreakCriteria) {
      const score = standing.tiebreakScores?.[criterion];
      const displayScore = score !== undefined ? (Number.isInteger(score) ? score : score.toFixed(1)) : '-';
      cells += `<td class="crosstable-tiebreak">${displayScore}</td>`;
    }

    rows += `<tr>${cells}</tr>`;
  });

  return `
    <div class="crosstable-container">
      <table class="crosstable">
        <thead>
          <tr>${headerCells}</tr>
        </thead>
        <tbody>
          ${rows}
        </tbody>
      </table>
    </div>
  `;
}

/**
 * Get CSS class based on result
 */
function getCellClass(results: string[]): string {
  if (results.length === 0) return '';

  // Check if all results are wins, losses, or draws
  const allWins = results.every(r => r === '1');
  const allLosses = results.every(r => r === '0');
  const allDraws = results.every(r => r === '½');

  if (allWins) return 'crosstable-win';
  if (allLosses) return 'crosstable-loss';
  if (allDraws) return 'crosstable-draw';
  return 'crosstable-mixed';
}

// Export to window for global access
window.renderCrosstable = renderCrosstable;
